import {Button} from '@/components/ui/button/Button'
import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useEditProjectDetails} from '@/features/projects/queries/useEditProjectDetails'
import {useProjectMutationSuccess} from '@/features/projects/hooks/useProjectMutationSuccess'
import {useTranslation} from '@/translations/i18n'
import {errorHandler, ObjectKeys} from '@/utilities/helpers'
import {useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {QUERY_KEYS} from '@/queryClient'
import {useQueryClient} from '@tanstack/react-query'
import {Spinner} from '@/components/ui/spinner/Spinner'

const EditProjectPatientFormFooterSchema = ProjectFieldsSchema.pick({
    id: true,
    patient: true,
    livingConditions: true,
    rehabilitationFacility: true
})
type EditProjectPatientFormFooterSchema = z.infer<typeof EditProjectPatientFormFooterSchema>

export const EditProjectPatientFormFooter = ({onCancel}: {onCancel: () => void}) => {
    const {t} = useTranslation()
    const form = useFormContext<EditProjectPatientFormFooterSchema>()
    const id = useWatch({control: form.control, name: 'id'})
    const patientId = useWatch({control: form.control, name: 'patient.id'})

    const handleProjectMutationSuccess = useProjectMutationSuccess({
        projectId: id,
        onComplete: onCancel
    })

    const queryClient = useQueryClient()
    const {mutate: editProjectDetails, isPending} = useEditProjectDetails({
        urlParams: {projectId: id},
        options: {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, id]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patientId]})
                await handleProjectMutationSuccess()
            },
            onError: errorHandler
        }
    })

    const handleSubmit = form.handleSubmit(formValues => {
        editProjectDetails({
            livingConditions: formValues.livingConditions,
            rehabilitationFacility: formValues.rehabilitationFacility
        })
    })

    return (
        <Flexbox fullWidth align="center" justify="end" gap={2}>
            <DialogDismiss onClick={onCancel} render={<Button variant="secondary" />}>
                {t('commons:close')}
            </DialogDismiss>
            <Button
                variant="primaryNeutral"
                onClick={handleSubmit}
                disabled={isPending || ObjectKeys(form.formState.dirtyFields).length == 0}
            >
                {t('commons:save')}
                {isPending && <Spinner />}
            </Button>
        </Flexbox>
    )
}
